// ?debug overlay and the window.__catDerby test hook.
import type { Camera, WebGLRenderer } from 'three';
import type { RaceEvent, RaceSnapshot } from '../race/types';
import type { QualityManager } from '../world/quality';
import type { SkyInfo } from '../world/environment';
import { FlickerProbe, type FlickerRecord } from './flickerProbe';

export type { BenchSummary as BenchResult } from './bench';
import type { BenchSummary as BenchResult } from './bench';

export interface CatDerbyHook {
  snapshot(): RaceSnapshot | null;
  events(): RaceEvent[];
  camera(): { position: [number, number, number]; fov: number; shot?: string; subject?: string | null; reason?: string; mode?: string } | null;
  quality(): { tier: string; renderScale: number; auto: boolean };
  bench: BenchResult | null;
  frames: number;
  /** The race source in use: kind, HUD label, live connection status and buffer ahead of render time. */
  race(): RaceInfo | null;
  /** The featured catnip trip: which cat, its beat and ms since the catch; null when none. */
  trip(): TripInfo | null;
  /** The race phase at render time, or null before the first snapshot. */
  phase(): RaceSnapshot['phase'] | null;
  /** Each cat in lane order: position on the track (0..1), place, what it is doing, and on the podium its behaviour. */
  cats(): CatInfo[];
  /** The sound: the long clip playing, the most recent original clips played (newest last) and what is sounding now. */
  audio(): AudioInfo | null;
  /** The ?seed in use (null when unseeded) and the ?moment asked for. */
  seed: number | null;
  moment: string | null;
  /** Recording times (ms) of the moments in the loaded replay, and the seek used; null before it loads. */
  moments(): { moments: Record<string, number | null>; seekMs: number } | null;
  /** With ?debug: short dark dips found in the drawn frames, newest last. */
  flickers(): FlickerRecord[];
  /** Joutseno time, sun and moon, light, season and holiday themes the stadium shows; null before it is built. */
  sky(): SkyInfo | null;
}

export type CatState = 'gates' | 'running' | 'eating' | 'tripping' | 'finished';
export interface CatInfo {
  id: string; lane: number; pos: number; place: number; state: CatState;
  /** On the podium: celebrate (the winner, first), sit, washPaw, washFace, lookAround, tailFlick, lieDown, stretch or stand; null elsewhere. */
  podium: string | null;
}
/** music: the long original clip playing (posttime, catmetal, trip) or none; cues: clip files played, newest last, with page time and race time;
 *  playing: clip files sounding now. */
export interface AudioInfo { unlocked: boolean; music: string; cues: { cue: string; at: number; raceT?: number }[]; playing?: string[]; volume?: number; soundOn?: boolean }

export interface TripInfo { cat: string; beat: string; elapsedMs: number; beatT: number }

export interface RaceInfo { kind: 'live' | 'replay'; label: string; connection: string; bufferAheadMs: number }
interface RaceInfoSource { kind: RaceInfo['kind']; label: string; connection: string; bufferAheadMs: number }

declare global { interface Window { __catDerby?: CatDerbyHook } }

const MAX_EVENTS = 500;

export class DebugHub {
  private lastSnapshot: RaceSnapshot | null = null;
  private eventLog: RaceEvent[] = [];
  private overlay: HTMLElement | null = null;
  private raceSource: RaceInfoSource | null = null;
  private audioState: (() => AudioInfo | null) | null = null;
  private momentInfo: { moments: Record<string, number | null>; seekMs: number } | null = null;
  private tripState: (() => TripInfo | null) | null = null;
  private podiumState: ((id: string) => string | null) | null = null;
  private skyState: (() => SkyInfo) | null = null;
  private directorState: (() => { shot: string; subject: string | null; reason: string; mode: string } | null) | null = null;
  private fpsAcc = 0; private fpsFrames = 0; private fps = 0;
  private probe: FlickerProbe | null = null;
  private recentDt: number[] = [];
  frames = 0;

  constructor(enabled: boolean, private quality: QualityManager, private getCamera: () => Camera | null,
    controls: { seed: number | null; moment: string | null } = { seed: null, moment: null }) {
    const hook: CatDerbyHook = {
      snapshot: () => this.lastSnapshot,
      events: () => [...this.eventLog],
      camera: () => {
        const c = this.getCamera() as (Camera & { fov?: number }) | null;
        if (!c) return null;
        const d = this.directorState?.();
        return { position: c.position.toArray() as [number, number, number], fov: c.fov ?? 0,
          ...(d ? { shot: d.shot, subject: d.subject, reason: d.reason, mode: d.mode } : {}) };
      },
      quality: () => ({ tier: this.quality.tier, renderScale: this.quality.renderScale, auto: this.quality.auto }),
      bench: null,
      get frames() { return 0; },
      race: () => {
        const r = this.raceSource;
        return r ? { kind: r.kind, label: r.label, connection: r.connection, bufferAheadMs: r.bufferAheadMs } : null;
      },
      trip: () => this.tripState?.() ?? null,
      phase: () => this.lastSnapshot?.phase ?? null,
      cats: () => (this.lastSnapshot ? catInfo(this.lastSnapshot, this.podiumState ?? undefined) : []),
      audio: () => this.audioState?.() ?? null,
      seed: controls.seed,
      moment: controls.moment,
      moments: () => this.momentInfo,
      flickers: () => [...(this.probe?.records ?? [])],
      sky: () => this.skyState?.() ?? null,
    };
    Object.defineProperty(hook, 'frames', { get: () => this.frames });
    window.__catDerby = hook; // always present: tests and the bench read it
    if (enabled) {
      this.overlay = document.createElement('pre');
      this.overlay.id = 'debug-overlay';
      document.body.appendChild(this.overlay);
      this.probe = new FlickerProbe();
    }
  }

  /** The camera director's current shot, shown by camera() and the overlay. */
  setDirector(fn: () => { shot: string; subject: string | null; reason: string; mode: string } | null): void {
    this.directorState = fn;
  }

  /** The stadium's time, light, season and themes, shown by sky() and the overlay. */
  setSky(fn: () => SkyInfo): void {
    this.skyState = fn;
  }

  setTrip(fn: () => TripInfo | null): void {
    this.tripState = fn;
  }

  /** What a cat is doing on the podium, by id; null when it is not there. */
  setPodium(fn: (id: string) => string | null): void {
    this.podiumState = fn;
  }

  setAudio(fn: () => AudioInfo | null): void {
    this.audioState = fn;
  }

  setMoments(moments: Record<string, number | null>, seekMs: number): void {
    this.momentInfo = { moments, seekMs };
  }

  setRace(source: RaceInfoSource): void {
    this.raceSource = source;
  }

  record(snapshot: RaceSnapshot, events: RaceEvent[]): void {
    this.lastSnapshot = snapshot;
    if (events.length) {
      this.eventLog.push(...events);
      if (this.eventLog.length > MAX_EVENTS) this.eventLog.splice(0, this.eventLog.length - MAX_EVENTS);
    }
  }

  frame(dtMs: number, renderer: WebGLRenderer): void {
    this.frames++;
    this.fpsAcc += dtMs; this.fpsFrames++;
    if (this.fpsAcc >= 500) { this.fps = (this.fpsFrames * 1000) / this.fpsAcc; this.fpsAcc = 0; this.fpsFrames = 0; }
    this.recentDt.push(Math.round(dtMs * 10) / 10);
    if (this.recentDt.length > 8) this.recentDt.shift();
    const dip = this.probe?.sample(renderer.domElement, this.frames, dtMs, () => this.flickerContext());
    if (dip) console.warn(`Cat Derby flicker: ${dip.frames} frame(s) ${(dip.depth * 100).toFixed(0)}% darker`, dip.context, dip);
    if (!this.overlay || this.frames % 6) return;
    const s = this.lastSnapshot;
    const r = renderer.info.render;
    const lines = [
      `fps ${this.fps.toFixed(0)}  tier ${this.quality.tier}  scale ${this.quality.renderScale.toFixed(2)}`,
      `draws ${r.calls}  tris ${r.triangles.toLocaleString()}  flickers ${this.probe?.records.length ?? 0}`,
      ((k) => k ? `Joutseno ${k.local}  sun ${k.sunElevation.toFixed(1)}° ${k.light}  ${k.themes.join(' ') || 'no theme'}` : '')(this.skyState?.()),
      ((d) => d ? `camera ${d.mode}: ${d.shot}${d.subject ? ' ' + d.subject : ''} (${d.reason})` : 'camera -')(this.directorState?.()),
      s ? `${s.source} race ${s.raceNo} ${s.phase} ${(s.phaseElapsedMs / 1000).toFixed(1)}s  signal ${s.signal}` : 'no snapshot',
      this.raceSource ? `${this.raceSource.connection}  buffer ${(this.raceSource.bufferAheadMs / 1000).toFixed(2)}s  ${this.raceSource.label}` : '',
      ...(s?.cats.map((c) => `${c.place} ${c.id.padEnd(6)} raw ${c.signalPos.toFixed(3)} disp ${c.displayPos.toFixed(3)} ${c.speed.toFixed(1)} m/s`) ?? []),
    ];
    this.overlay.textContent = lines.join('\n');
  }

  private flickerContext(): Record<string, unknown> {
    const s = this.lastSnapshot;
    const t = s?.t ?? 0;
    return {
      camera: this.directorState?.() ?? null,
      trip: this.tripState?.() ?? null,
      phase: s?.phase ?? null,
      signal: s?.signal ?? null,
      quality: { tier: this.quality.tier, renderScale: this.quality.renderScale },
      recentFrameMs: [...this.recentDt],
      recentEvents: this.eventLog.filter((e) => e.t >= t - 1500).map((e) => e.type),
      hidden: document.hidden,
    };
  }

  setBench(result: BenchResult): void {
    if (window.__catDerby) window.__catDerby.bench = result;
  }
}

/** What each cat is doing, for tests. Pure. */
export function catInfo(s: RaceSnapshot, podium?: (id: string) => string | null): CatInfo[] {
  return s.cats.map((c) => ({
    id: c.id, lane: c.lane, pos: c.signalPos, place: c.place,
    state: c.finished || s.phase === 'finished' ? 'finished' : s.phase === 'starting' ? 'gates'
      : c.eating ? 'eating' : c.tripping ? 'tripping' : 'running',
    podium: podium?.(c.id) ?? null,
  }));
}
